<script setup lang="ts">
import TextButton from '@/components/input/Btn/TextButton.vue';
import ToggleSwitch from '@/corePagesModule/components/ToggleSwitch.vue';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { openConfirmModal, openFailToSavedModal, openSuccessToSaveModal } from '@/corePagesModule/components/modal/modalActions';
import { SettingRepository } from '@/coreInfrastructureModule/Repositories/SettingRepository';
import type { ApiBusinessFreeColumnSetting } from '@/coreInfrastructureModule/interfaces/ISettingRepository';
import { i18n } from '@/i18n';
import SettingsPageTitle from '@/settingsModule/components/SettingsPageTitle.vue';
import TextInput from '@/unmanagedBusinessModule/components/TextInput.vue';
import { onBeforeMount, reactive, watch } from 'vue';

const DEFAULT_HEADER_NAME_PREFIX = '自由欄';

const t = i18n.t;
const repository = new SettingRepository();
let loadedState: ApiBusinessFreeColumnSetting | undefined;
const state = reactive<ApiBusinessFreeColumnSetting>({
  aggregate: [
    { headerName: '自由欄1', isMulti: false, isConfigured: false },
    { headerName: '自由欄2', isMulti: false, isConfigured: false },
    { headerName: '自由欄3', isMulti: false, isConfigured: false },
  ],
  base: [
    { headerName: '自由欄1', isMulti: false, isConfigured: false },
    { headerName: '自由欄2', isMulti: false, isConfigured: false },
    { headerName: '自由欄3', isMulti: false, isConfigured: false },
  ],
});

/**
 * 自由欄設定をコピーする
 * @param setting - コピー元の自由欄設定
 */
const cloneSetting = (setting: ApiBusinessFreeColumnSetting): ApiBusinessFreeColumnSetting => ({
  aggregate: setting.aggregate.map((item) => ({ ...item })),
  base: setting.base.map((item) => ({ ...item })),
});

/**
 * 読み込み時点で有効だった自由欄が無効化されたかどうかを判定する
 */
const hasDisabledConfiguredColumn = () => {
  if (!loadedState) return false;
  const snapshot = loadedState;
  return (
    snapshot.aggregate.some((item, index) => item.isConfigured && !state.aggregate[index]?.isConfigured) ||
    snapshot.base.some((item, index) => item.isConfigured && !state.base[index]?.isConfigured)
  );
};

/**
 * 自由欄設定を読み込む
 */
const loadSetting = async () => {
  const result = await waitLoading(repository.getBusinessFreeColumnSetting());
  if (!result || result.status.type !== 'success') return;
  state.aggregate = result.status.data.aggregate;
  state.base = result.status.data.base;
  loadedState = cloneSetting(result.status.data);
};

/**
 * 自由欄設定を保存する
 */
const saveSetting = async () => {
  if (hasDisabledConfiguredColumn()) {
    const confirmResult = await openConfirmModal(
      'warning',
      t('自由欄を無効化しますか'),
      t('削除して保存'),
      t('キャンセル'),
      t('無効化した自由欄の入力済みデータも削除されます。')
    );
    if (!confirmResult.isConfirmed) return;
  }

  const result = await waitLoading(repository.putBusinessFreeColumnSetting({ aggregate: state.aggregate, base: state.base }));
  if (result?.status.type === 'success') {
    loadedState = cloneSetting(state);
    await openSuccessToSaveModal();
  } else {
    await openFailToSavedModal();
  }
};

onBeforeMount(loadSetting);

watch(
  () => i18n.locale,
  () => {
    state.aggregate.forEach((column, index) => {
      if (!column.isConfigured) {
        column.headerName = `${t(DEFAULT_HEADER_NAME_PREFIX)}${index + 1}`;
      }
    });
    state.base.forEach((column, index) => {
      if (!column.isConfigured) {
        column.headerName = `${t(DEFAULT_HEADER_NAME_PREFIX)}${index + 1}`;
      }
    });
  }
);
</script>

<template>
  <section class="business-free-columns-page">
    <SettingsPageTitle :label="t('設定 > 自由欄カラム')" />
    <div class="panel">
      <p class="panel__description">{{ t('組織で利用する自由欄のヘッダー名と複数選択設定を管理します') }}</p>
      <div class="section">
        <h2 class="section__title">{{ t('仕入取引に関する項目を追加') }}</h2>
        <div v-for="(item, index) in state.aggregate" :key="`aggregate-${index}`" class="row">
          <label class="row__check" :title="t('使用')">
            <input
              type="checkbox"
              class="row__check-input"
              :checked="item.isConfigured"
              @change="(event) => (item.isConfigured = (event.target as HTMLInputElement).checked)"
            />
          </label>
          <span class="row__index">{{ t('ヘッダー名') }}</span>
          <div class="row__input">
            <TextInput
              :model-value="item.headerName"
              label=""
              :disabled="!item.isConfigured"
              @update:model-value="(value) => (item.headerName = value)"
            />
          </div>
          <div class="row__toggle">
            <ToggleSwitch :is-enabled="item.isMulti" :disabled="!item.isConfigured" @on-toggled="(value) => (item.isMulti = value)">
              {{ t('複数選択') }}
            </ToggleSwitch>
          </div>
        </div>
      </div>
      <div class="section">
        <h2 class="section__title">{{ t('販売取引に関する項目を追加') }}</h2>
        <div v-for="(item, index) in state.base" :key="`base-${index}`" class="row">
          <label class="row__check" :title="t('使用')">
            <input
              type="checkbox"
              class="row__check-input"
              :checked="item.isConfigured"
              @change="(event) => (item.isConfigured = (event.target as HTMLInputElement).checked)"
            />
          </label>
          <span class="row__index">{{ t('ヘッダー名') }}</span>
          <div class="row__input">
            <TextInput
              :model-value="item.headerName"
              label=""
              :disabled="!item.isConfigured"
              @update:model-value="(value) => (item.headerName = value)"
            />
          </div>
          <div class="row__toggle">
            <ToggleSwitch :is-enabled="item.isMulti" :disabled="!item.isConfigured" @on-toggled="(value) => (item.isMulti = value)">
              {{ t('複数選択') }}
            </ToggleSwitch>
          </div>
        </div>
      </div>
      <div class="actions">
        <TextButton @click="saveSetting">{{ t('保存') }}</TextButton>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.business-free-columns-page {
  display: grid;
  gap: 24px;
  padding: 24px;
}
.panel {
  display: grid;
  gap: 24px;
  background: white;
  border-radius: 8px;
  padding: 24px;
}
.panel__description {
  margin: 0;
  color: $textDefault;
}
.section {
  display: grid;
  gap: 12px;
}
.section__title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: $textDefault;
}
.row {
  display: grid;
  grid-template-columns: 38px 80px minmax(0, 320px) auto;
  align-items: center;
  gap: 16px;
}
.row__check {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.row__check-input {
  width: 20px;
  height: 20px;
  margin: 0;
  cursor: pointer;
  accent-color: $semanticPrimary;
}
.row__index {
  font-size: 14px;
  font-weight: 700;
  color: $textDefault;
}
.row__input {
  width: 100%;
}
.row__input :deep(.text-input) {
  border: 1px solid $borderDefault;
  border-radius: 6px;
}
.row__input :deep(.text-input):focus {
  border-color: $semanticPrimary;
  outline: none;
}
.row__toggle {
  justify-self: start;
}
.actions {
  display: flex;
  justify-content: flex-start;
}
@media (max-width: 640px) {
  .row {
    grid-template-columns: 1fr;
  }
}
</style>
